import {
  AfterViewChecked,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { Spinner } from '@app/shared/components/spinner/spinner';
import {
  ConfirmPopup,
  ConfirmPopupConfig
} from '@app/shared/components/confirm-popup/confirm-popup';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import {
  AiHelpAskContent,
  AiHelpClarifyContent,
  AiHelpClarifyItem,
  AiHelpConfirmContent,
  AiHelpConfirmSummaryObject,
  AiHelpConversationItem,
  AiHelpDataContent,
  AiHelpDataEmailPreviewContent,
  AiHelpDataProjectItem,
  AiHelpDataProjectHealthContent,
  AiHelpDataUpdateItem,
  AiHelpDataWorkTodayItem,
  AiHelpGuideContent,
  AiHelpGuideLink,
  AiHelpMessage,
  AiHelpMessageContent,
  AiHelpOffTopicContent,
  AiHelpReplyAction
} from '../../models/ai-help-assistant.models';
import { AiHelpAssistantApiService } from '../../services/ai-help-assistant-api.service';
import { AiHelpAssistantStateService } from '../../services/ai-help-assistant-state.service';
import { AiHelpContextService } from '../../services/ai-help-context.service';
import {
  AiHelpSpeechRecognitionService,
  AiHelpSpeechTranscriptEvent
} from '../../services/ai-help-speech-recognition.service';
import {
  AiHelpSpeechSynthesisService,
  hasExpandableAlternatives,
  isExpandableGuideMethod
} from '../../services/ai-help-speech-synthesis.service';
import {
  formatClarifySelectionLabel,
  formatDataLabel,
  formatRecordFieldValue,
  formatDailyHoursDate,
  formatHours,
  formatKeyValueLabel,
  formatRankingEntryValue,
  formatPercentComplete,
  formatDaysOverdue,
  formatOverdueCount,
  formatDaysRemaining,
  isCompleteProjectHealth,
  isDisplayableDataKey,
  formatDataBlockStatValue as formatStatCardValue,
  resolveBlocksItemLabel,
  resolveBlocksScopeLabel,
  serializeClarifySelection
} from '@app/features/utils/ai-help-response.parser';
import {
  AI_HELP_ASSISTANT_HOME_STATIC,
  AiHelpHomeActionCard,
  AiHelpHomeTabKey,
  TIM_HOW_I_WORK_PAGE_PATH,
  TIM_ASSISTANT_HOVER_LABEL,
  getAiHelpTimeBasedGreeting
} from '../../constants/ai-help-assistant-home-static';
import {
  AiHelpDataBlock,
  AiHelpDataItemListItem,
  AiHelpDataItemListOverProject,
  AiHelpDataItemListTicket,
  AiHelpPromptExampleCategory,
  AiHelpPromptExampleItem
} from '../../models/ai-help-assistant.models';
import {
  getAvatarColorFromSeed,
  getInitialsFromName,
  resolveAvatarUrl
} from '@app/pages/dashboard/widgets/shared/dashboard-avatar.util';
import {
  FestivalType,
  getActiveFestivalType
} from '@app/shared/components/festival-decoration/festival-decoration';

export type AiHelpEventTheme = 'fall-of-summer' | 'thanksgiving' | 'halloween' | 'remembrance';

const DEFAULT_TIM_ICON = 'assets/images/tim-b3.png';
const EVENT_TIM_ICONS: Record<AiHelpEventTheme, string> = {
  'fall-of-summer': 'assets/images/events/tim-fall.png',
  thanksgiving: 'assets/images/events/tim-thanks.png',
  halloween: 'assets/images/events/tim-hallo.png',
  remembrance: 'assets/images/events/tim-rem.png'
};

function resolveAiHelpEventTheme(date = new Date()): AiHelpEventTheme | null {
  switch (getActiveFestivalType(date)) {
    case FestivalType.AUTUMN:
      return 'fall-of-summer';
    case FestivalType.THANKSGIVING:
      return 'thanksgiving';
    case FestivalType.HALLOWEEN:
      return 'halloween';
    case FestivalType.REMEMBRANCE:
      return 'remembrance';
    default:
      return null;
  }
}

@Component({
  selector: 'app-ai-help-assistant-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    Spinner,
    ConfirmPopup,
    ...matTooltipSmartImports
  ],
  templateUrl: './ai-help-assistant-panel.html',
  styleUrl: './ai-help-assistant-panel.scss'
})
export class AiHelpAssistantPanel implements AfterViewChecked, OnDestroy {
  readonly state = inject(AiHelpAssistantStateService);
  readonly context = inject(AiHelpContextService);
  readonly speech = inject(AiHelpSpeechRecognitionService);
  readonly speechOutput = inject(AiHelpSpeechSynthesisService);
  private readonly auth = inject(AuthService);
  private readonly api = inject(AiHelpAssistantApiService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly toastr = inject(ToastrService);
  readonly hasExpandableAlternatives = hasExpandableAlternatives;
  readonly isExpandableGuideMethod = isExpandableGuideMethod;
  /** True while `POST /download-pdf` is in flight for a data reply action. */
  readonly downloadingDataPdf = signal(false);
  /** Email preview whose `POST /send-email` is in flight. */
  readonly sendingEmail = signal<AiHelpDataEmailPreviewContent | null>(null);
  /** Email previews already sent in this session (Send button becomes "Sent"). */
  readonly sentEmails = signal<ReadonlySet<AiHelpDataEmailPreviewContent>>(new Set());
  /**
   * Cached iframe `srcdoc` per preview: a new SafeHtml instance on every change
   * detection would reload the iframe in a loop.
   */
  private readonly emailPreviewDocs = new WeakMap<AiHelpDataEmailPreviewContent, SafeHtml>();

  // Static "home" UI content (until backend drives the data one-by-one).
  readonly home = AI_HELP_ASSISTANT_HOME_STATIC;
  readonly howIWorkPageUrl = `${window.location.origin}/#${TIM_HOW_I_WORK_PAGE_PATH}`;
  /** Public Privacy Policy URL (no login). Hash route: `/#/privacy`. */
  readonly privacyPageUrl = `${window.location.origin}/#/privacy`;
  readonly timHoverLabel = TIM_ASSISTANT_HOVER_LABEL;
  readonly eventTheme = resolveAiHelpEventTheme();
  readonly headerTimIconSrc = this.eventTheme
    ? EVENT_TIM_ICONS[this.eventTheme]
    : DEFAULT_TIM_ICON;
  readonly headerEventLeaves = [
    { src: 'assets/images/events/maple1.png', left: '6%', size: '16px', duration: '4.2s', delay: '0s', drift: '12px', spin: '220deg' },
    { src: 'assets/images/events/maple2.png', left: '20%', size: '14px', duration: '4.8s', delay: '0.5s', drift: '-14px', spin: '-190deg' },
    { src: 'assets/images/events/maple3.png', left: '34%', size: '18px', duration: '4.4s', delay: '1s', drift: '10px', spin: '250deg' },
    { src: 'assets/images/events/head-maple.png', left: '48%', size: '15px', duration: '5s', delay: '0.2s', drift: '-8px', spin: '-170deg' },
    { src: 'assets/images/events/maple1.png', left: '62%', size: '17px', duration: '4.6s', delay: '0.8s', drift: '14px', spin: '200deg' },
    { src: 'assets/images/events/maple2.png', left: '76%', size: '14px', duration: '5.1s', delay: '1.3s', drift: '-11px', spin: '-210deg' },
    { src: 'assets/images/events/maple3.png', left: '88%', size: '16px', duration: '4.7s', delay: '1.6s', drift: '9px', spin: '180deg' },
  ] as const;
  readonly headerEventBats = [
    { left: '46%', top: '16%', size: '18px', duration: '5.2s', delay: '0s', path: 'a' },
    { left: '66%', top: '38%', size: '15px', duration: '6s', delay: '0.5s', path: 'b' },
    { left: '82%', top: '12%', size: '17px', duration: '5.6s', delay: '1.1s', path: 'c' },
  ] as const;
  readonly headerEventPoppies = [
    { src: 'assets/images/events/rem-fl.png', left: '8%', size: '10px', duration: '4.4s', delay: '0s', drift: '10px', spin: '180deg' },
    { src: 'assets/images/events/remember-fl.png', left: '24%', size: '11px', duration: '5s', delay: '0.5s', drift: '-12px', spin: '-160deg' },
    { src: 'assets/images/events/rem-fl.png', left: '42%', size: '9px', duration: '4.6s', delay: '1s', drift: '8px', spin: '200deg' },
    { src: 'assets/images/events/remember-fl.png', left: '60%', size: '10px', duration: '5.1s', delay: '0.3s', drift: '-9px', spin: '-190deg' },
    { src: 'assets/images/events/rem-fl.png', left: '78%', size: '11px', duration: '4.8s', delay: '1.3s', drift: '12px', spin: '170deg' },
    { src: 'assets/images/events/remember-fl.png', left: '90%', size: '9px', duration: '5.2s', delay: '0.8s', drift: '-8px', spin: '-210deg' },
  ] as const;
  readonly activeHomeTab = signal<AiHelpHomeTabKey>('ask');
  readonly isSamplePromptsOpen = signal(false);
  readonly isHistoryOpen = signal(false);
  readonly samplePromptsQuery = signal('');
  readonly expandedSampleCategoryIds = signal<ReadonlySet<string>>(new Set());
  readonly highlightedCategoryId = signal<string | null>(null);
  private readonly pendingHistoryDelete = signal<AiHelpConversationItem | null>(null);
  private readonly historyDeleteMode = signal<'single' | 'all' | null>(null);
  readonly showHistoryDeleteConfirm = computed(() => this.historyDeleteMode() !== null);

  /** Confirm-popup config for deleting one or all history conversations. */
  readonly historyDeleteConfirmConfig = computed((): ConfirmPopupConfig => {
    if (this.historyDeleteMode() === 'all') {
      return {
        title: 'Delete all conversations',
        message:
          'Are you sure you want to delete all conversations from history? This action cannot be undone.',
        type: 'delete',
        showCancel: true,
        cancelText: 'Cancel',
        confirmText: 'Delete all',
        loading: this.state.isDeletingAllConversations()
      };
    }

    const item = this.pendingHistoryDelete();
    const title = item?.title?.trim() || 'this conversation';
    const conversationId = item?.conversationId?.trim() || '';
    return {
      title: 'Delete conversation',
      message: `Are you sure you want to delete "${title}" from history? This action cannot be undone.`,
      type: 'delete',
      showCancel: true,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      loading: conversationId ? this.state.isDeletingConversation(conversationId) : false
    };
  });

  /** Sample Prompts panel chrome, with static fallbacks for first paint. */
  readonly samplePromptsChrome = computed(() => {
    const api = this.state.promptExamples();
    const fallback = this.home.samplePrompts;
    return {
      title: api?.title || fallback.title,
      subtitle: api?.subtitle || fallback.subtitle,
      searchPlaceholder: api?.searchPlaceholder || fallback.searchPlaceholder,
      lessLabel: api?.lessLabel || fallback.lessLabel
    };
  });

  /** Home quick actions; prefer API payload, keep static fallback for first paint / failures. */
  readonly popularActions = computed(() => {
    const api = this.state.dashboardPopularActions();
    return api?.length ? api : this.home.popularActions;
  });

  /** Home "Try asking" prompts; prefer API payload, keep static fallback for first paint / failures. */
  readonly tryAsk = computed(() => {
    const api = this.state.dashboardTryAsk();
    return api?.items?.length ? api : this.home.tryAsk;
  });

  /** Past conversations for the history side drawer. */
  readonly historyConversations = computed((): AiHelpConversationItem[] => {
    return this.state.conversations() ?? [];
  });

  /** Filtered sample-prompt categories for the left “See more” panel. */
  readonly filteredSampleCategories = computed((): AiHelpPromptExampleCategory[] => {
    const categories =
      this.state.promptExamples()?.categories ??
      this.home.samplePrompts.categories;
    const query = this.samplePromptsQuery().trim().toLowerCase();
    if (!query) {
      return categories;
    }

    return categories
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            item.label.toLowerCase().includes(query) || item.prompt.toLowerCase().includes(query)
        )
      }))
      .filter((category) => category.items.length > 0);
  });

  /** Time-of-day greeting; refreshes when the home view is shown. */
  readonly greetingTitle = computed(() => {
    this.state.isHomeView();
    return getAiHelpTimeBasedGreeting();
  });

  /** First name from logged-in user (AuthService / localStorage). No backend call needed. */
  readonly greetingName = computed(() => {
    return this.getUserFirstName() || this.home.greeting.name;
  });

  /** Full display name for user message avatars. */
  readonly userDisplayName = computed(() => {
    this.auth.userData();
    return this.resolveStoredUserName();
  });

  readonly userAvatarUrl = computed(() => {
    this.auth.userData();
    return resolveAvatarUrl(
      this.auth.userData(),
      typeof localStorage !== 'undefined' ? { image: localStorage.getItem('userImage') } : null
    );
  });

  readonly showUserAvatarImage = computed(
    () => !!this.userAvatarUrl() && !this.userAvatarImageFailed()
  );

  readonly userAvatarSeed = computed(() => {
    this.auth.userData();
    return (
      (this.auth.userData()?._id as string | undefined)?.trim() ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('userID')?.trim() : '') ||
      this.resolveStoredUserName()
    );
  });

  private readonly userAvatarImageFailed = signal(false);
  @ViewChild('messageList') private messageListRef?: ElementRef<HTMLElement>;
  @ViewChild('composerInput') private composerInputRef?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('promptsPanelBody') private promptsPanelBodyRef?: ElementRef<HTMLElement>;

  continuousVoiceMode = false;
  readonly isMobileViewport = signal(false);
  readonly isExpanded = signal(false);
  private readonly injector = inject(Injector);
  private readonly expandedMethods = signal<Record<string, number[]>>({});
  private readonly selectedClarifyGroups = signal<Record<string, string | null>>({});
  private readonly askSelectedDates = signal<Record<string, Date | null>>({});
  private readonly askCalendarConfigs = new Map<
    string,
    { min: Date | null; max: Date | null; startAt: Date }
  >();
  private shouldScrollToBottom = false;
  private voiceBaseDraft = '';
  private shouldAutoSendVoice = false;
  private draftFromVoice = false;
  private lastVoiceContinuationMessageId: string | null = null;
  private mobileExpandMq: MediaQueryList | null = null;
  private readonly onMobileExpandMqChange = (event: MediaQueryListEvent): void => {
    this.syncMobileViewport(event.matches);
  };

  constructor() {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mobileExpandMq = window.matchMedia('(max-width: 767px)');
      this.syncMobileViewport(this.mobileExpandMq.matches);
      this.mobileExpandMq.addEventListener('change', this.onMobileExpandMqChange);
    }

    effect(() => {
      if (!this.state.isOpen()) {
        this.stopVoiceInput(false);
        this.continuousVoiceMode = false;
        this.lastVoiceContinuationMessageId = null;
        this.isExpanded.set(false);
        this.closeSamplePromptsPanel();
        this.closeHistoryPanel();
        this.closeHistoryDeleteConfirm();
        return;
      }

      if (this.isMobileViewport()) {
        this.isExpanded.set(true);
      }

      // Ready to type as soon as the panel opens — no extra click needed.
      this.focusComposer();
    });

    effect(() => {
      this.userAvatarUrl();
      untracked(() => this.userAvatarImageFailed.set(false));
    });

    effect(() => {
      const categoryId = this.highlightedCategoryId();
      const open = this.isSamplePromptsOpen();
      const categories =
        this.state.promptExamples()?.categories ?? this.home.samplePrompts.categories;
      if (!open || !categoryId || !categories.some((category) => category.id === categoryId)) {
        return;
      }

      untracked(() => this.scrollHighlightedCategoryIntoView());
    });

    effect(() => {
      if (this.state.isLoading()) {
        this.stopVoiceInput(false);
        return;
      }

      // Restore caret after the answer finishes (focus can be lost during the request).
      if (this.state.isOpen()) {
        this.focusComposer();
      }
    });

    effect(() => {
      this.handleClarifyVoiceContinuation();
    });

    effect(() => {
      this.syncExpandedMethodsWithSpeech();
    });

    // Keep the latest reply in view, including tall project/clarify lists.
    effect(() => {
      const open = this.state.isOpen();
      const messages = this.state.messages();
      if (!open || messages.length === 0) {
        return;
      }

      const last = messages[messages.length - 1];
      void last?.id;
      void last?.content;
      void last?.pending;
      void this.state.isLoading();

      untracked(() => this.scheduleScrollToBottom());
    });
  }

  onComposerShellClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, mat-icon, textarea')) {
      return;
    }
    this.focusComposer();
  }

  ngAfterViewChecked(): void {
    if (!this.shouldScrollToBottom) {
      return;
    }
    this.shouldScrollToBottom = false;
    this.scrollMessageListToBottom();
  }

  ngOnDestroy(): void {
    this.hideTimNameTipPortal();
    this.mobileExpandMq?.removeEventListener('change', this.onMobileExpandMqChange);
    this.stopVoiceInput(false);
    this.speechOutput.stop();
  }

  /** Body-level tip so it is not clipped by panel overflow/transform. */
  onTimNameTipEnter(event: Event): void {
    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor) {
      return;
    }
    this.showTimNameTipPortal(anchor);
  }

  onTimNameTipLeave(): void {
    this.hideTimNameTipPortal();
  }

  private timNameTipPortal: HTMLDivElement | null = null;

  private showTimNameTipPortal(anchor: HTMLElement): void {
    this.hideTimNameTipPortal();

    const tip = document.createElement('div');
    tip.className = 'tim-name-tip-portal';
    tip.setAttribute('role', 'tooltip');
    tip.innerHTML = '<strong>TIM</strong> (TechWyse Intelligent Manager)';
    document.body.appendChild(tip);

    const anchorRect = anchor.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(
        anchorRect.left + anchorRect.width / 2 - tipRect.width / 2,
        window.innerWidth - tipRect.width - 8
      )
    );
    tip.style.top = `${Math.round(anchorRect.bottom + 8)}px`;
    tip.style.left = `${Math.round(left)}px`;

    requestAnimationFrame(() => {
      tip.classList.add('tim-name-tip-portal--visible');
    });

    this.timNameTipPortal = tip;
  }

  private hideTimNameTipPortal(): void {
    if (!this.timNameTipPortal) {
      return;
    }
    this.timNameTipPortal.remove();
    this.timNameTipPortal = null;
  }

  private scheduleScrollToBottom(): void {
    this.shouldScrollToBottom = true;
    this.scrollMessageListToBottom();

    afterNextRender(
      () => {
        this.scrollMessageListToBottom();
        requestAnimationFrame(() => this.scrollMessageListToBottom());
      },
      { injector: this.injector }
    );
  }

  private scrollMessageListToBottom(): void {
    const el = this.messageListRef?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }

  onClose(): void {
    this.continuousVoiceMode = false;
    this.isExpanded.set(false);
    this.closeSamplePromptsPanel();
    this.closeHistoryPanel();
    this.closeHistoryDeleteConfirm();
    this.stopVoiceInput(false);
    this.state.close();
  }

  onToggleExpand(): void {
    if (this.isMobileViewport()) {
      return;
    }
    this.isExpanded.update((expanded) => !expanded);
  }

  onHomeTabClick(tab: AiHelpHomeTabKey): void {
    this.activeHomeTab.set(tab);
  }

  onHomeActionClick(card: AiHelpHomeActionCard): void {
    this.activeHomeTab.set(card.tab);
    this.openSamplePromptsForCategory(card.id);
  }

  isHomeActionHighlighted(cardId: string): boolean {
    const highlighted = this.highlightedCategoryId();
    if (!highlighted) {
      return false;
    }
    return this.resolvePromptCategoryId(cardId) === highlighted;
  }

  onOpenSamplePrompts(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.openSamplePromptsForCategory(null);
  }

  onToggleSamplePrompts(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isSamplePromptsOpen()) {
      this.closeSamplePromptsPanel();
      return;
    }
    this.closeHistoryPanel();
    this.openSamplePromptsForCategory(null);
  }

  onToggleHistory(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isHistoryOpen()) {
      this.closeHistoryPanel();
      return;
    }
    this.closeSamplePromptsPanel();
    this.isHistoryOpen.set(true);
  }

  onCloseHistory(): void {
    this.closeHistoryPanel();
  }

  onHistoryItemClick(item: AiHelpConversationItem): void {
    const conversationId = item?.conversationId?.trim();
    if (!conversationId) {
      return;
    }
    this.closeHistoryPanel();
    this.state.loadConversation(conversationId);
    this.shouldScrollToBottom = true;
  }

  onHistoryItemDelete(item: AiHelpConversationItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const conversationId = item?.conversationId?.trim();
    if (
      !conversationId ||
      this.state.isDeletingConversation(conversationId) ||
      this.state.isDeletingAllConversations()
    ) {
      return;
    }

    this.pendingHistoryDelete.set(item);
    this.historyDeleteMode.set('single');
  }

  onHistoryDeleteAll(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.historyConversations().length || this.state.isDeletingAllConversations()) {
      return;
    }

    this.pendingHistoryDelete.set(null);
    this.historyDeleteMode.set('all');
  }

  onConfirmHistoryDelete(): void {
    if (this.historyDeleteMode() === 'all') {
      if (this.state.isDeletingAllConversations()) {
        return;
      }
      this.state.deleteAllConversations();
      this.closeHistoryDeleteConfirm();
      return;
    }

    const item = this.pendingHistoryDelete();
    const conversationId = item?.conversationId?.trim();
    if (!conversationId || this.state.isDeletingConversation(conversationId)) {
      return;
    }

    this.state.deleteConversation(conversationId);
    this.closeHistoryDeleteConfirm();
  }

  onCancelHistoryDelete(): void {
    this.closeHistoryDeleteConfirm();
  }

  private closeHistoryDeleteConfirm(): void {
    this.historyDeleteMode.set(null);
    this.pendingHistoryDelete.set(null);
  }

  formatHistoryTimestamp(value: string | undefined): string {
    const raw = value?.trim();
    if (!raw) {
      return '';
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const now = new Date();
    return parsed.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: parsed.getFullYear() === now.getFullYear() ? undefined : 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  /**
   * Opens the public How I work? player in a new tab.
   * Shareable URL: `/#/watch/tim-how-i-work` (no login required).
   */
  onHowIWorkClick(event: Event): void {
    event.stopPropagation();
  }

  /** Opens the public Privacy Policy page in a new tab. Does not intercept the native href. */
  onPrivacyPolicyClick(event: Event): void {
    event.stopPropagation();
  }

  private openSamplePromptsForCategory(categoryId: string | null): void {
    const resolvedCategoryId = categoryId ? this.resolvePromptCategoryId(categoryId) : null;
    this.closeHistoryPanel();
    this.samplePromptsQuery.set('');
    this.expandedSampleCategoryIds.set(new Set());
    this.highlightedCategoryId.set(resolvedCategoryId);
    this.state.ensurePromptExamples();
    this.isSamplePromptsOpen.set(true);

    if (resolvedCategoryId) {
      this.scrollHighlightedCategoryIntoView();
    } else {
      this.scrollPromptsPanelToTop();
    }
  }

  private resolvePromptCategoryId(categoryId: string): string {
    const categories =
      this.state.promptExamples()?.categories ?? this.home.samplePrompts.categories;
    if (categories.some((category) => category.id === categoryId)) {
      return categoryId;
    }

    const homeCard = this.home.actions.find((card) => card.id === categoryId);
    if (!homeCard) {
      return categoryId;
    }

    const byLabel = categories.find(
      (category) => category.title.trim().toLowerCase() === homeCard.label.trim().toLowerCase()
    );
    if (byLabel) {
      return byLabel.id;
    }

    const byTab = categories.find((category) => category.id === homeCard.tab);
    return byTab?.id ?? categoryId;
  }

  private scrollPromptsPanelToTop(): void {
    afterNextRender(
      () => {
        const root = this.promptsPanelBodyRef?.nativeElement;
        if (root) {
          root.scrollTop = 0;
        }
      },
      { injector: this.injector }
    );
  }

  private scrollHighlightedCategoryIntoView(): void {
    afterNextRender(
      () => {
        const categoryId = this.highlightedCategoryId();
        if (!categoryId) {
          return;
        }

        const root = this.promptsPanelBodyRef?.nativeElement;
        const el =
          root?.querySelector<HTMLElement>(
            `.ai-help-prompts-panel__category[data-category-id="${CSS.escape(categoryId)}"]`
          ) ??
          document.querySelector<HTMLElement>(
            `.ai-help-prompts-panel__category[data-category-id="${CSS.escape(categoryId)}"]`
          );

        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      },
      { injector: this.injector }
    );
  }

  onRetryPromptExamples(): void {
    this.state.retryPromptExamples();
  }

  onCloseSamplePrompts(): void {
    this.closeSamplePromptsPanel();
  }

  onSamplePromptsQueryChange(value: string): void {
    this.samplePromptsQuery.set(value);
  }

  onToggleSampleCategory(categoryId: string): void {
    this.expandedSampleCategoryIds.update((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  }

   onAttentionItemClick(prompt: string): void {
    const value = prompt?.trim();
    if (!value || this.state.isLoading()) {
      return;
    }

    this.continuousVoiceMode = false;
    this.lastVoiceContinuationMessageId = null;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendSuggestion(value);
    this.focusComposer();
  }

  isSampleCategoryExpanded(categoryId: string): boolean {
    if (this.samplePromptsQuery().trim()) {
      return true;
    }
    return this.expandedSampleCategoryIds().has(categoryId);
  }

  private samplePreviewLimit(category: AiHelpPromptExampleCategory): number {
    const count = Number(category.previewCount);
    if (Number.isFinite(count) && count >= 1) {
      return Math.floor(count);
    }
    return category.items.length;
  }

  visibleSamplePromptItems(category: AiHelpPromptExampleCategory) {
    if (this.isSampleCategoryExpanded(category.id)) {
      return category.items;
    }
    return category.items.slice(0, this.samplePreviewLimit(category));
  }

  samplePromptItemTrackId(item: AiHelpPromptExampleItem, index: number): string {
    return `${item.id}-${index}`;
  }

  canExpandSampleCategory(category: AiHelpPromptExampleCategory): boolean {
    return category.items.length > this.samplePreviewLimit(category) && !this.samplePromptsQuery().trim();
  }

  sampleCategoryMoreLabel(category: AiHelpPromptExampleCategory): string {
    if (this.isSampleCategoryExpanded(category.id)) {
      return this.samplePromptsChrome().lessLabel;
    }

    const remaining = Math.max(0, category.items.length - this.samplePreviewLimit(category));
    if (remaining <= 0) {
      return '';
    }

    return remaining === 1 ? '1 more question' : `${remaining} more questions`;
  }

  onSamplePromptClick(prompt: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeSamplePromptsPanel();
    this.fillComposer(prompt);
  }

  /**
   * Put a prompt into the composer for editing — do not send until the user confirms.
   */
  private fillComposer(prompt: string): void {
    const text = prompt?.trim();
    if (!text) {
      return;
    }

    this.continuousVoiceMode = false;
    this.lastVoiceContinuationMessageId = null;
    this.stopVoiceInput(false);
    this.draftFromVoice = false;
    this.state.setDraft(text);
    this.resizeComposer();
    this.focusComposer();
  }

  private closeSamplePromptsPanel(): void {
    this.isSamplePromptsOpen.set(false);
    this.samplePromptsQuery.set('');
    this.expandedSampleCategoryIds.set(new Set());
    this.highlightedCategoryId.set(null);
  }

  private closeHistoryPanel(): void {
    this.isHistoryOpen.set(false);
  }

  private syncMobileViewport(isMobile: boolean): void {
    this.isMobileViewport.set(isMobile);
    if (isMobile && this.state.isOpen()) {
      this.isExpanded.set(true);
    }
  }

  onNewChat(): void {
    this.continuousVoiceMode = false;
    this.lastVoiceContinuationMessageId = null;
    this.expandedMethods.set({});
    this.selectedClarifyGroups.set({});
    this.askSelectedDates.set({});
    this.askCalendarConfigs.clear();
    this.stopVoiceInput(false);
    this.draftFromVoice = false;
    this.activeHomeTab.set('ask');
    this.closeSamplePromptsPanel();
    this.closeHistoryPanel();
    this.state.startNewChat();
    this.focusComposer();
  }

  onBackToHome(): void {
    this.continuousVoiceMode = false;
    this.lastVoiceContinuationMessageId = null;
    this.stopVoiceInput(false);
    this.draftFromVoice = false;
    this.closeSamplePromptsPanel();
    this.closeHistoryPanel();
    this.state.showHome();
    this.focusComposer();
  }

   onReturnToThread(): void {
    this.closeSamplePromptsPanel();
    this.closeHistoryPanel();
    this.state.showThread();
    this.shouldScrollToBottom = true;
    this.focusComposer();
  }

  onDraftChange(value: string): void {
    this.state.setDraft(value);
    this.resizeComposer();
  }

  onComposerInput(): void {
    this.resizeComposer();
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  onSend(isAudio = false): void {
    if (!this.state.canSend()) {
      return;
    }

    const fromVoice = isAudio || this.draftFromVoice;
    this.continuousVoiceMode = fromVoice;

    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendDraft(fromVoice);
    this.draftFromVoice = false;
    this.focusComposer();
  }

  onSuggestionClick(prompt: string): void {
    // Home / popular / try prompts: fill the input so the user can edit before sending.
    this.fillComposer(prompt);
  }

  onToggleVoiceInput(): void {
    if (this.state.isLoading() || this.speech.isProcessing()) {
      return;
    }

    // Stop / finalize the current capture.
    if (this.speech.isListening()) {
      // MediaRecorder needs an explicit stop to upload + transcribe.
      if (this.speech.engine() === 'media-recorder') {
        this.speech.stop();
        return;
      }

      // Web Speech: cancel the in-progress recognition.
      this.continuousVoiceMode = false;
      this.lastVoiceContinuationMessageId = null;
      this.stopVoiceInput(false);
      return;
    }

    // Mic: start recording voice into the composer.
    this.continuousVoiceMode = true;
    this.startVoiceInput();
  }

  voiceInputTooltip(): string {
    if (!this.speech.isSupported()) {
      return 'Voice input is not supported in this browser';
    }
    if (this.speech.isProcessing()) {
      return 'Transcribing…';
    }
    if (this.speech.isListening()) {
      return this.speech.engine() === 'media-recorder'
        ? 'Listening… tap to stop early'
        : 'Stop recording';
    }
    return 'Ask with voice';
  }

  isInternalGuideLink(link: AiHelpGuideLink): boolean {
    return this.getInternalRoute(link.url) !== null;
  }

  getInternalRoute(url: string): string | null {
    try {
      const parsed = new URL(url);
      if (parsed.hash.startsWith('#/')) {
        return parsed.hash.slice(1);
      }
    } catch {
      return null;
    }
    return null;
  }

  onGuideLinkClick(): void {
    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.state.close();
  }

  toGuideContent(content: AiHelpMessageContent | undefined): AiHelpGuideContent | null {
    return content?.kind === 'guide' ? content : null;
  }

  toClarifyContent(content: AiHelpMessageContent | undefined): AiHelpClarifyContent | null {
    return content?.kind === 'clarify' ? content : null;
  }

  toDataContent(content: AiHelpMessageContent | undefined): AiHelpDataContent | null {
    return content?.kind === 'data' ? content : null;
  }

  toConfirmContent(content: AiHelpMessageContent | undefined): AiHelpConfirmContent | null {
    return content?.kind === 'confirm' ? content : null;
  }

  toAskContent(content: AiHelpMessageContent | undefined): AiHelpAskContent | null {
    return content?.kind === 'ask' ? content : null;
  }

  toOffTopicContent(content: AiHelpMessageContent | undefined): AiHelpOffTopicContent | null {
    return content?.kind === 'off_topic' ? content : null;
  }

  onReplyActionClick(action: AiHelpReplyAction, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (action.action === 'open_sample_prompts') {
      this.openSamplePromptsForCategory(null);
    }
  }

  /** Material icon for a data-reply action button. */
  dataReplyActionIcon(action: AiHelpReplyAction): string {
    switch (action.action) {
      case 'download_pdf':
        return 'picture_as_pdf';
      case 'send_email':
        return 'email';
      case 'confirm_send_email':
        return 'send';
      case 'open_sample_prompts':
        return 'auto_awesome';
    }
  }

  isDataReplyActionBusy(action: AiHelpReplyAction, dataReply?: AiHelpDataContent): boolean {
    if (action.action === 'download_pdf') {
      return this.downloadingDataPdf();
    }
    if (action.action === 'send_email') {
      return this.state.isLoading();
    }
    if (action.action === 'confirm_send_email') {
      const email = dataReply?.emailPreviewContent;
      return !email || this.sendingEmail() !== null || this.sentEmails().has(email);
    }
    return false;
  }

  dataReplyActionLabel(action: AiHelpReplyAction, dataReply: AiHelpDataContent): string {
    if (action.action === 'confirm_send_email') {
      const email = dataReply.emailPreviewContent;
      if (email && this.sendingEmail() === email) {
        return 'Sending…';
      }
      if (email && this.sentEmails().has(email)) {
        return 'Sent';
      }
    }
    return action.label;
  }

  emailPreviewDoc(email: AiHelpDataEmailPreviewContent): SafeHtml {
    let doc = this.emailPreviewDocs.get(email);
    if (!doc) {
      // Rendered only inside a sandboxed iframe (no allow-scripts), so inline styles are kept.
      doc = this.sanitizer.bypassSecurityTrustHtml(
        '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">' +
          '<style>html,body{margin:0}body{padding:12px;font-family:Arial,Helvetica,sans-serif;' +
          'font-size:13px;line-height:1.45;color:#1f2937;overflow-x:auto;word-break:break-word}' +
          'img{max-width:100%;height:auto}p{margin:0 0 10px}h3{margin:14px 0 6px;font-size:14px}' +
          'ul{margin:0 0 10px;padding-left:20px}th,td{vertical-align:top;font-size:12px}</style>' +
          `</head><body>${email.previewHtml}</body></html>`
      );
      this.emailPreviewDocs.set(email, doc);
    }
    return doc;
  }

  /** Grow the preview iframe to its content height (same-origin srcdoc, no scripts). */
  onEmailPreviewLoad(event: Event): void {
    const frame = event.target as HTMLIFrameElement | null;
    const doc = frame?.contentDocument;
    if (!frame || !doc?.body) {
      return;
    }
    frame.style.height = `${doc.documentElement.scrollHeight + 2}px`;
  }

  onDataReplyActionClick(
    action: AiHelpReplyAction,
    dataReply: AiHelpDataContent,
    event?: Event
  ): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (action.action === 'open_sample_prompts') {
      this.openSamplePromptsForCategory(null);
      return;
    }

    if (action.action === 'download_pdf') {
      this.downloadDataReplyPdf(dataReply);
      return;
    }

    if (action.action === 'send_email') {
      this.sendDataReplyAskFollowUp(action.label || 'Send Email');
      return;
    }

    if (action.action === 'confirm_send_email' && dataReply.emailPreviewContent) {
      this.sendEmailPreview(dataReply.emailPreviewContent);
    }
  }

  private sendEmailPreview(email: AiHelpDataEmailPreviewContent): void {
    if (this.sendingEmail() || this.sentEmails().has(email)) {
      return;
    }

    this.sendingEmail.set(email);
    this.api.sendEmail(email.raw).subscribe({
      next: (res: any) => {
        this.sendingEmail.set(null);
        if (res?.success === false) {
          this.toastr.error(res?.message || 'Failed to send email');
          return;
        }
        this.sentEmails.update((set) => new Set(set).add(email));
        this.toastr.success(res?.message || 'Email sent successfully');
      },
      error: (err: any) => {
        this.sendingEmail.set(null);
        this.toastr.error(
          err?.error?.message || err?.error?.detail || err?.message || 'Failed to send email'
        );
      }
    });
  }

  /** Continue the thread with a follow-up `/ask` question (e.g. Send Email). */
  private sendDataReplyAskFollowUp(question: string): void {
    const prompt = question.trim();
    if (!prompt || this.state.isLoading()) {
      return;
    }

    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendSuggestion(prompt);
    this.focusComposer();
  }

  private downloadDataReplyPdf(dataReply: AiHelpDataContent): void {
    if (this.downloadingDataPdf()) {
      return;
    }

    this.downloadingDataPdf.set(true);
    const conversationId = this.state.activeConversationId()?.trim() || null;
    const apiBlocks = dataReply.apiData?.['blocks'];
    const body: Record<string, unknown> = {
      conversation_id: conversationId,
      reply_type: 'data',
      intro: dataReply.intro,
      text: dataReply.intro,
      blocks: Array.isArray(apiBlocks) ? apiBlocks : dataReply.blocks ?? []
    };

    this.api.downloadPdf(body).subscribe({
      next: (blob) => {
        this.triggerBlobDownload(blob, this.buildDataPdfFileName());
        this.downloadingDataPdf.set(false);
      },
      error: () => {
        this.downloadingDataPdf.set(false);
      }
    });
  }

  private buildDataPdfFileName(): string {
    const stamp = new Date().toISOString().slice(0, 10);
    return `ai-help-report-${stamp}.pdf`;
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  confirmSummaryEntries(confirm: AiHelpConfirmContent): Array<{ key: string; value: string }> {
    return Object.entries(confirm.summary)
      .filter(
        ([key, value]) =>
          isDisplayableDataKey(key) &&
          (typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean' ||
            value === null) &&
          String(value ?? '').trim() !== ''
      )
      .map(([key, value]) => ({
        key,
        value: formatRecordFieldValue(key, value as string | number | boolean | null)
      }));
  }

  confirmSummaryList(
    confirm: AiHelpConfirmContent,
    key: string
  ): AiHelpConfirmSummaryObject[] {
    const value = confirm.summary[key];
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter(
      (item): item is AiHelpConfirmSummaryObject =>
        !!item && typeof item === 'object' && !Array.isArray(item)
    );
  }

  confirmSummaryItemEntries(
    item: AiHelpConfirmSummaryObject
  ): Array<{ key: string; value: string }> {
    return Object.entries(item)
      .filter(
        ([key, value]) =>
          isDisplayableDataKey(key) &&
          (typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean' ||
            value === null) &&
          String(value ?? '').trim() !== ''
      )
      .map(([key, value]) => ({
        key,
        value: formatRecordFieldValue(key, value as string | number | boolean | null)
      }));
  }

  hasConfirmSummaryList(confirm: AiHelpConfirmContent, key: string): boolean {
    return this.confirmSummaryList(confirm, key).length > 0;
  }

  confirmSummaryScalarValue(confirm: AiHelpConfirmContent, key: string): string {
    const value = confirm.summary[key];
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null
    ) {
      return formatRecordFieldValue(key, value);
    }
    return '';
  }

  isConfirmSelectionEnabled(messageId: string): boolean {
    return this.isSelectableAssistantMessage(messageId, 'confirm');
  }

  isAskSelectionEnabled(messageId: string): boolean {
    return this.isSelectableAssistantMessage(messageId, 'ask');
  }

  askSelectedDate(messageId: string): Date | null {
    return this.askSelectedDates()[messageId] ?? null;
  }

  getAskCalendarConfig(
    messageId: string,
    ask: AiHelpAskContent
  ): { min: Date | null; max: Date | null; startAt: Date } {
    const cached = this.askCalendarConfigs.get(messageId);
    if (cached) {
      return cached;
    }

    const today = this.startOfDay(new Date());
    const config = ask.dateRange
      ? {
          min: this.parseIsoDateLocal(ask.dateRange.from),
          max: this.parseIsoDateLocal(ask.dateRange.to),
          startAt: this.parseIsoDateLocal(ask.dateRange.from)
        }
      : {
          min: today,
          max: null,
          startAt: today
        };

    this.askCalendarConfigs.set(messageId, config);
    return config;
  }

  onAskDateSelected(messageId: string, date: Date | null): void {
    if (!date || !this.isAskSelectionEnabled(messageId)) {
      return;
    }

    this.askSelectedDates.update((state) => ({ ...state, [messageId]: date }));
    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendSuggestion(this.formatAskDate(date));
    this.focusComposer();
  }

  private formatAskDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private parseIsoDateLocal(iso: string): Date {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  onConfirmClick(messageId: string, confirmed: boolean): void {
    if (!this.isConfirmSelectionEnabled(messageId)) {
      return;
    }

    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendSuggestion(confirmed ? 'Yes, confirm' : 'No, cancel');
    this.focusComposer();
  }

  dataFieldEntries(data: AiHelpDataContent): Array<{ key: string; value: string }> {
    return Object.entries(data.data)
      .filter(([key]) => isDisplayableDataKey(key))
      .map(([key, value]) => ({
        key,
        value: formatRecordFieldValue(key, value)
      }));
  }

  /** Detect empty count/client scalar replies for the metric + client-chip layout. */
  asCountClientMetric(
    fields: Array<{ key: string; value: string }>
  ): { count: string; client: string | null; label: string } | null {
    if (!fields.length || fields.length > 3) {
      return null;
    }

    const byKey = new Map(fields.map((field) => [field.key.trim().toLowerCase(), field.value]));
    if (!byKey.has('count')) {
      return null;
    }

    const allowed = new Set(['count', 'client']);
    if ([...byKey.keys()].some((key) => !allowed.has(key))) {
      return null;
    }

    return {
      count: byKey.get('count') ?? '0',
      client: byKey.get('client')?.trim() || null,
      label: 'Count'
    };
  }

  /**
   * Detect a compact count (+ optional client) layout inside `blocks`
   * so it can reuse the metric strip UI from the design.
   */
  asBlocksCountClientMetric(
    blocks: AiHelpDataBlock[] | undefined,
    data?: Record<string, string | number | boolean | null>
  ): { count: string; client: string | null; label: string } | null {
    if (!blocks?.length || blocks.length > 2) {
      return null;
    }

    let count: string | null = null;
    let label = 'Count';
    let client: string | null = null;

    for (const block of blocks) {
      if (block.type === 'stat_cards') {
        if (block.items.length !== 1) {
          return null;
        }
        const item = block.items[0];
        const itemLabel = item.label.trim().toLowerCase();
        if (itemLabel !== 'count' && itemLabel !== 'total') {
          return null;
        }
        count = formatStatCardValue(item.value);
        label = item.label.trim() || 'Count';
        continue;
      }

      if (block.type === 'key_values') {
        for (const row of block.rows) {
          const rowLabel = row.label.trim().toLowerCase();
          if (rowLabel === 'client') {
            client = String(row.value ?? '').trim() || null;
            continue;
          }
          return null;
        }
        continue;
      }

      return null;
    }

    if (count == null) {
      return null;
    }

    if (!client && data) {
      const entries = Object.entries(data);
      for (const [key, value] of entries) {
        if (key.trim().toLowerCase() === 'client' && value != null && String(value).trim()) {
          client = String(value).trim();
          break;
        }
      }
    }

    return { count, client, label };
  }

  clientInitial(name: string): string {
    const trimmed = (name || '').trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
  }

  formatDataFieldLabel(key: string): string {
    return formatDataLabel(key);
  }

  readonly formatDataBlockStatValue = formatStatCardValue;

  itemListMetaTone(priority: string): 'neutral' | 'red' | 'green' | 'amber' {
    const normalized = priority.trim().toLowerCase();
    if (
      normalized === 'high' ||
      normalized === 'critical' ||
      normalized === 'blocked' ||
      normalized === 'overdue'
    ) {
      return 'red';
    }
    if (
      normalized === 'medium' ||
      normalized === 'warning' ||
      normalized === 'warn' ||
      normalized === 'in progress' ||
      normalized === 'in-progress'
    ) {
      return 'amber';
    }
    if (
      normalized === 'low' ||
      normalized === 'complete' ||
      normalized === 'completed' ||
      normalized === 'closed' ||
      normalized === 'ok'
    ) {
      return 'green';
    }
    return 'neutral';
  }

  itemListMetaParts(priority: string): { label: string; detail?: string } {
    const parts = priority
      .split(/\s*[·|]\s*/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (parts.length >= 2) {
      return { label: parts[0], detail: parts.slice(1).join(' · ') };
    }
    return { label: priority.trim() };
  }

  /** Used / monthly hours for a nested `over_projects` row. */
  itemListOverProjectHours(project: AiHelpDataItemListOverProject): string | null {
    if (project.usedHours != null && project.monthlyHours != null) {
      return `${formatHours(project.usedHours)} / ${formatHours(project.monthlyHours)}`;
    }
    if (project.usedHours != null) {
      return formatHours(project.usedHours);
    }
    if (project.monthlyHours != null) {
      return formatHours(project.monthlyHours);
    }
    return null;
  }

  /** Overage caption for a nested `over_projects` row. */
  itemListOverProjectMeta(project: AiHelpDataItemListOverProject): string | null {
    if (project.overage == null || !Number.isFinite(project.overage)) {
      return null;
    }
    const abs = Math.abs(project.overage);
    if (project.overage > 0) {
      return `+${formatHours(abs)} over`;
    }
    if (project.overage < 0) {
      return `${formatHours(abs)} under`;
    }
    return `${formatHours(0)} over`;
  }

  /** Utilization percent from API, or used / monthly when percent is omitted. */
  itemListOverProjectPlotPercent(project: AiHelpDataItemListOverProject): number | null {
    if (project.utilizationPct != null && Number.isFinite(project.utilizationPct)) {
      return project.utilizationPct;
    }
    if (
      project.usedHours != null &&
      project.monthlyHours != null &&
      project.monthlyHours > 0 &&
      Number.isFinite(project.usedHours)
    ) {
      return (project.usedHours / project.monthlyHours) * 100;
    }
    return null;
  }

  itemListOverProjectTone(percent: number): 'neutral' | 'red' | 'amber' {
    if (percent > 100) {
      return 'red';
    }
    if (percent >= 90) {
      return 'amber';
    }
    return 'neutral';
  }

  /** Whether an `item_list` row should use the stacked (nested) layout. */
  itemListRowHasDetails(item: AiHelpDataItemListItem): boolean {
    return (
      !!item.overProjects?.length ||
      !!item.tickets?.length ||
      item.delayBusinessDays != null ||
      !!item.originalDate ||
      !!item.projectedDate ||
      item.totalHours != null ||
      item.billableHours != null ||
      item.aiHours != null ||
      item.submitted != null ||
      !!item.status ||
      !!item.assignees ||
      !!item.link
    );
  }

  /** Caption for grouped ticket rows under an `item_list` client / account. */
  itemListTicketCountMeta(item: AiHelpDataItemListItem): string | null {
    const count = item.tickets?.length ?? 0;
    if (count <= 0) {
      return null;
    }
    return count === 1 ? '1 ticket' : `${count} tickets`;
  }

  itemListTicketTypeLabel(ticket: AiHelpDataItemListTicket): string {
    return (ticket.ticketType || '').trim();
  }

  /** Delay caption for late / delayed `item_list` rows. */
  itemListDelayMeta(item: AiHelpDataItemListItem): string | null {
    if (item.delayBusinessDays == null || !Number.isFinite(item.delayBusinessDays)) {
      return null;
    }
    const days = Math.round(item.delayBusinessDays);
    return days === 1 ? '1 business day late' : `${days} business days late`;
  }

  /** Original → projected date range for delayed rows. */
  itemListDelayDateRange(item: AiHelpDataItemListItem): string | null {
    const original = item.originalDate ? formatDailyHoursDate(item.originalDate) : '';
    const projected = item.projectedDate ? formatDailyHoursDate(item.projectedDate) : '';
    if (original && projected) {
      return `${original} → ${projected}`;
    }
    if (original) {
      return `Original ${original}`;
    }
    if (projected) {
      return `Projected ${projected}`;
    }
    return null;
  }

  /**
   * Relative delay bar within an `item_list` block (share of the max delay in that list).
   */
  itemListDelayPlotPercent(
    item: AiHelpDataItemListItem,
    items: AiHelpDataItemListItem[]
  ): number | null {
    if (item.delayBusinessDays == null || !Number.isFinite(item.delayBusinessDays)) {
      return null;
    }
    const maxDelay = items.reduce((max, entry) => {
      if (entry.delayBusinessDays == null || !Number.isFinite(entry.delayBusinessDays)) {
        return max;
      }
      return Math.max(max, entry.delayBusinessDays);
    }, 0);
    if (maxDelay <= 0) {
      return null;
    }
    return (item.delayBusinessDays / maxDelay) * 100;
  }

  /** Whether an `item_list` row carries timesheet hours fields. */
  itemListHasHoursFields(item: AiHelpDataItemListItem): boolean {
    return (
      item.totalHours != null ||
      item.billableHours != null ||
      item.aiHours != null ||
      item.submitted != null
    );
  }

  /** Tone for timesheet submitted / not-submitted chips. */
  itemListSubmittedTone(submitted: boolean | undefined): 'green' | 'red' | null {
    if (submitted === true) {
      return 'green';
    }
    if (submitted === false) {
      return 'red';
    }
    return null;
  }

  formatProjectPercent(value: number | undefined): string {
    return value == null ? '' : formatPercentComplete(value);
  }

  formatProjectDaysOverdue(value: number | undefined): string {
    return value == null ? '' : formatDaysOverdue(value);
  }

  formatProjectOverdueCount(value: number | undefined): string {
    return value == null ? '' : formatOverdueCount(value);
  }

  formatProjectDaysRemaining(value: number | undefined): string {
    return value == null ? '' : formatDaysRemaining(value);
  }

  shouldShowHealthDaysRemaining(health: AiHelpDataProjectHealthContent): boolean {
    return health.daysRemaining != null && !isCompleteProjectHealth(health);
  }

  formatRankingHours(value: number): string {
    return formatHours(value);
  }

  readonly formatRankingEntryValue = formatRankingEntryValue;
  readonly formatKeyValueLabel = formatKeyValueLabel;

  /** Item unit label from a sibling `key_values` block (e.g. `open tasks`). */
  blocksRankingItemLabel(blocks: AiHelpDataBlock[] | undefined): string | undefined {
    return resolveBlocksItemLabel(blocks);
  }

  /** Scope from a sibling `key_values` block for ranking summary. */
  blocksRankingScope(blocks: AiHelpDataBlock[] | undefined): string | undefined {
    return resolveBlocksScopeLabel(blocks);
  }

  /** Prefer API `percent`, otherwise nothing (parser derives share for hours/open_items). */
  rankingPlotPercent(entry: { percent?: number }): number | null {
    return entry.percent != null && Number.isFinite(entry.percent) ? entry.percent : null;
  }

  /** Cell text for a `table` block row (`prop` → value). */
  tableCellValue(row: Record<string, string>, prop: string): string {
    const value = row[prop]?.trim();
    return value || '—';
  }

  /** Format key-value display (dates stay readable). */
  formatKeyValueDisplay(label: string, value: string): string {
    const trimmedLabel = label.trim();
    const trimmedValue = value.trim();
    if (
      /^\d{4}-\d{2}-\d{2}/.test(trimmedValue) &&
      /(date|start|completion|due|planned|projected)/i.test(trimmedLabel)
    ) {
      return formatDailyHoursDate(trimmedValue) || value;
    }
    return value;
  }

  /** Project / portfolio id from a `key_values` block (used for routing, hidden in UI). */
  keyValuesProjectId(
    block: Extract<AiHelpDataBlock, { type: 'key_values' }>
  ): string | null {
    const row = block.rows.find((entry) =>
      /^(project|portfolio)\s*id$/i.test(entry.label.trim())
    );
    const id = row?.value?.trim();
    return id || null;
  }

  /** Internal project details route when a `key_values` block includes a project id. */
  keyValuesProjectRoute(
    block: Extract<AiHelpDataBlock, { type: 'key_values' }>
  ): string | null {
    const projectId = this.keyValuesProjectId(block);
    return projectId ? `/projects/project-details/${projectId}` : null;
  }

  isKeyValueProjectNameRow(label: string): boolean {
    return /^project\s*name$/i.test(label.trim());
  }

  /** True for a `Link` key-values row. */
  isKeyValueLinkRow(label: string): boolean {
    return /^link$/i.test(label.trim());
  }

  /** True for a bare `Name` key-values row (paired with a sibling `Link`). */
  isKeyValueNameRow(label: string): boolean {
    return /^name$/i.test(label.trim());
  }

  /**
   * Internal app route or external href from a URL / path value.
   * Hash URLs like `https://host/#/projects/...` become `/projects/...`.
   */
  keyValueLinkTarget(value: string): { route?: string; href?: string } | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const internal = this.getInternalRoute(trimmed);
    if (internal) {
      return { route: internal };
    }

    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      return { route: trimmed };
    }

    // Single URL only (project names may embed URLs with spaces).
    if (/^https?:\/\/\S+$/i.test(trimmed)) {
      return { href: trimmed };
    }

    return null;
  }

  /** `Link` row target from the same `key_values` block, when present. */
  keyValuesBlockLinkTarget(
    block: Extract<AiHelpDataBlock, { type: 'key_values' }>
  ): { route?: string; href?: string } | null {
    const linkRow = block.rows.find((entry) => this.isKeyValueLinkRow(entry.label));
    return linkRow ? this.keyValueLinkTarget(linkRow.value) : null;
  }

  /**
   * Click target for a key-values row:
   * - `Link` → its own value
   * - `Name` → sibling `Link` when present
   * - any other row whose value is a single URL / app path
   */
  keyValueRowLinkTarget(
    row: { label: string; value: string },
    block: Extract<AiHelpDataBlock, { type: 'key_values' }>
  ): { route?: string; href?: string } | null {
    if (this.isKeyValueLinkRow(row.label)) {
      return this.keyValueLinkTarget(row.value);
    }
    if (this.isKeyValueNameRow(row.label)) {
      return this.keyValuesBlockLinkTarget(block);
    }
    return this.keyValueLinkTarget(row.value);
  }

  /** In-app route for a clickable key-values row, if any. */
  keyValueRowLinkRoute(
    row: { label: string; value: string },
    block: Extract<AiHelpDataBlock, { type: 'key_values' }>
  ): string | null {
    return this.keyValueRowLinkTarget(row, block)?.route ?? null;
  }

  /** External href for a clickable key-values row, if any. */
  keyValueRowLinkHref(
    row: { label: string; value: string },
    block: Extract<AiHelpDataBlock, { type: 'key_values' }>
  ): string | null {
    return this.keyValueRowLinkTarget(row, block)?.href ?? null;
  }

  /** Short label for a clickable `Link` row (avoids dumping the full URL). */
  keyValueLinkDisplay(label: string, value: string): string {
    if (this.isKeyValueLinkRow(label)) {
      return 'Open';
    }
    return this.formatKeyValueDisplay(label, value);
  }

  /**
   * Rows shown for a `key_values` block.
   * Hides ranking metadata (scope / item label), item-list metric labels, and project id (routing only).
   */
  visibleKeyValueRows(
    block: Extract<AiHelpDataBlock, { type: 'key_values' }>,
    blocks: AiHelpDataBlock[] | undefined
  ): Array<{ label: string; value: string }> {
    const hasRanking = !!blocks?.some((entry) => entry.type === 'ranking');
    const hasItemList = !!blocks?.some((entry) => entry.type === 'item_list');

    return block.rows.filter((row) => {
      const label = row.label.trim();
      if (/^(project|portfolio)\s*id$/i.test(label)) {
        return false;
      }
      if (hasRanking && /^(scope(\s*label)?|item\s*label)$/i.test(label)) {
        return false;
      }
      if (hasItemList && /^metric$/i.test(label)) {
        return false;
      }
      return true;
    });
  }

  formatDailyHoursDateLabel(value: string): string {
    return formatDailyHoursDate(value);
  }

  workTodayItemTrackId(item: AiHelpDataWorkTodayItem, index: number): string {
    return item.link || `${item.name}-${index}`;
  }

  aiHoursTrendBarWidth(hours: number, weeks: Array<{ hours: number }>): number {
    if (!weeks.length) {
      return 0;
    }

    const maxHours = Math.max(...weeks.map((week) => week.hours));
    if (maxHours <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, (hours / maxHours) * 100));
  }

  /** Scheduled segment width for `free_hours` capacity plots (overbooked is not plotted). */
  freeHoursScheduledWidth(scheduledHours: number, capacityHours: number | undefined): number {
    if (capacityHours == null || capacityHours <= 0 || !Number.isFinite(scheduledHours)) {
      return 0;
    }
    return Math.max(0, Math.min(100, (scheduledHours / capacityHours) * 100));
  }

  /** Free segment width; remaining capacity after scheduled, capped so the bar stays ≤ 100%. */
  freeHoursAvailableWidth(
    scheduledHours: number,
    freeHours: number,
    capacityHours: number | undefined
  ): number {
    if (capacityHours == null || capacityHours <= 0 || !Number.isFinite(freeHours)) {
      return 0;
    }
    const scheduledPct = this.freeHoursScheduledWidth(scheduledHours, capacityHours);
    const freePct = Math.max(0, (freeHours / capacityHours) * 100);
    return Math.max(0, Math.min(100 - scheduledPct, freePct));
  }

  projectProgressWidth(value: number | undefined): number {
    if (value == null || !Number.isFinite(value)) {
      return 0;
    }
    return Math.max(0, Math.min(100, value));
  }

  clientBriefProgressTone(value: number | undefined): 'high' | 'medium' | 'low' {
    if (value == null || !Number.isFinite(value)) {
      return 'low';
    }
    if (value >= 75) {
      return 'high';
    }
    if (value >= 25) {
      return 'medium';
    }
    return 'low';
  }

  clientBriefProjectStatusClass(status: string | undefined): Record<string, boolean> {
    const normalized = (status ?? '').trim().toLowerCase();
    return {
      'ai-help-panel__client-brief-status--recurring': normalized.includes('recurring'),
      'ai-help-panel__client-brief-status--current': normalized.includes('current'),
      'ai-help-panel__client-brief-status--complete':
        normalized.includes('complete') || normalized.includes('closed')
    };
  }

  /** Split leading date from a client-brief activity line for the timeline date chip. */
  parseClientBriefActivity(activity: string): { date: string | null; text: string } {
    const raw = (activity || '').trim();
    if (!raw) {
      return { date: null, text: '' };
    }

    const match = raw.match(
      /^((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.?\s+\d{1,2}(?:,?\s+\d{2,4})?)\s*[—–\-:]\s*(.+)$/i
    );
    if (match?.[1] && match?.[2]) {
      return { date: match[1].trim(), text: match[2].trim() };
    }

    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})\s*[—–\-:]\s*(.+)$/);
    if (isoMatch?.[1] && isoMatch?.[2]) {
      return { date: this.formatDailyHoursDateLabel(isoMatch[1]), text: isoMatch[2].trim() };
    }

    return { date: null, text: raw };
  }

  selectedClarifyGroup(messageId: string): string | null {
    return this.selectedClarifyGroups()[messageId] ?? null;
  }

  selectClarifyGroup(messageId: string, group: string): void {
    this.selectedClarifyGroups.update((state) => ({ ...state, [messageId]: group }));
    this.scheduleScrollToBottom();
  }

  clearClarifyGroup(messageId: string): void {
    this.selectedClarifyGroups.update((state) => ({ ...state, [messageId]: null }));
  }

  clarifyGroupItems(
    clarify: AiHelpClarifyContent,
    groupName: string | null
  ): AiHelpClarifyItem[] {
    if (!groupName || !clarify.options?.length) {
      return [];
    }
    return clarify.options.find((option) => option.group === groupName)?.items ?? [];
  }

  clarifyItemTrackId(item: AiHelpClarifyItem, index: number): string {
    let baseId = '';
    if (item.kind === 'project') {
      baseId = item.projectId;
    } else if (item.kind === 'person') {
      baseId = item.userId;
    } else if (item.kind === 'template') {
      baseId = item.templateId;
    } else if (item.kind === 'approval') {
      baseId = item.approvalId;
    } else if (item.kind === 'service') {
      baseId = item.serviceId;
    } else if (item.kind === 'service_category') {
      baseId = item.serviceCategoryId;
    } else {
      baseId = item.taskId;
    }

    return `${item.kind}-${baseId}-${index}`;
  }

  isClarifySelectionEnabled(messageId: string): boolean {
    return this.isSelectableAssistantMessage(messageId, 'clarify');
  }

  /** Clarify option lists (grouped or string choices) stay selectable in prior replies. */
  isClarifyOptionsListEnabled(messageId: string): boolean {
    if (this.state.isLoading()) {
      return false;
    }

    const message = this.state.messages().find((entry) => entry.id === messageId);
    if (!message || message.role !== 'assistant' || message.pending) {
      return false;
    }

    const content = message.messageContent;
    return (
      content?.kind === 'clarify' &&
      !!(content.options?.length || content.choices?.length)
    );
  }

  isDataSelectionEnabled(messageId: string): boolean {
    return this.isSelectableAssistantMessage(messageId, 'data');
  }

  onClarifyItemClick(item: AiHelpClarifyItem): void {
    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendSuggestion(
      serializeClarifySelection(item),
      formatClarifySelectionLabel(item)
    );
    this.focusComposer();
  }

  onClarifyChoiceClick(choice: string): void {
    const value = choice.trim();
    if (!value) {
      return;
    }

    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendSuggestion(value);
    this.focusComposer();
  }

  onDataProjectClick(project: AiHelpDataProjectItem): void {
    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    const prompt = project.projectId
      ? JSON.stringify({ project_id: project.projectId, name: project.projectName })
      : project.projectName;
    this.state.sendSuggestion(prompt, project.projectName);
    this.focusComposer();
  }

  onDataUpdateClick(update: AiHelpDataUpdateItem): void {
    this.continuousVoiceMode = false;
    this.stopVoiceInput(false);
    this.shouldScrollToBottom = true;
    this.state.sendSuggestion(this.formatDataUpdateSelection(update));
    this.focusComposer();
  }

  private isSelectableAssistantMessage(
    messageId: string,
    kind: 'clarify' | 'data' | 'confirm' | 'ask'
  ): boolean {
    if (this.state.isLoading()) {
      return false;
    }

    const last = this.state.messages().at(-1);
    return (
      last?.id === messageId &&
      last.role === 'assistant' &&
      !last.pending &&
      last.messageContent?.kind === kind
    );
  }

  private formatDataUpdateSelection(update: AiHelpDataUpdateItem): string {
    const on = update.on ?? update.type ?? 'update';
    const date = update.date ? ` (${update.date})` : '';
    return `Show full detail for ${on}${date}`;
  }

  isMethodExpanded(messageId: string, methodIndex: number): boolean {
    return (this.expandedMethods()[messageId] ?? []).includes(methodIndex);
  }

  toggleMethodExpansion(
    messageId: string,
    methodIndex: number,
    content?: AiHelpMessageContent
  ): void {
    const wasExpanded = this.isMethodExpanded(messageId, methodIndex);

    this.expandedMethods.update((state: Record<string, number[]>) => {
      const current = new Set(state[messageId] ?? []);
      if (current.has(methodIndex)) {
        current.delete(methodIndex);
      } else {
        current.add(methodIndex);
      }
      return { ...state, [messageId]: [...current] };
    });

    if (wasExpanded || content?.kind !== 'guide' || !content.isAudio) {
      return;
    }

    this.speechOutput.speakGuideMethod(messageId, content, methodIndex);
  }

  shouldShowMethodSteps(messageId: string, guide: AiHelpGuideContent, methodIndex: number): boolean {
    const method = guide.methods[methodIndex];
    if (!method || !isExpandableGuideMethod(method)) {
      return true;
    }

    return this.isMethodExpanded(messageId, methodIndex);
  }

  onToggleMessageAudio(messageId: string, content: AiHelpMessageContent | undefined): void {
    if (!content?.isAudio) {
      return;
    }

    if (this.speechOutput.isSpeakingMessage(messageId)) {
      this.speechOutput.stop();
      return;
    }

    this.speechOutput.speakMessage(messageId, content);
  }

  messageAudioTooltip(messageId: string, isAudio?: boolean): string {
    if (!isAudio || !this.speechOutput.isSupported()) {
      return 'Audio response';
    }
    if (this.speechOutput.isSpeakingMessage(messageId)) {
      return 'Stop audio';
    }
    return 'Play audio';
  }

  isSpeakingStep(messageId: string, methodIndex: number, stepIndex: number): boolean {
    return this.speechOutput.isSpeakingStep(messageId, methodIndex, stepIndex);
  }

  private syncExpandedMethodsWithSpeech(): void {
    const messageId = this.speechOutput.activeMessageId();
    const part = this.speechOutput.activePart();
    if (!messageId || !part) {
      return;
    }

    if (part.partKind === 'step' && part.methodIndex !== undefined) {
      this.expandedMethods.update((state: Record<string, number[]>) => {
        const current = state[messageId] ?? [];
        if (current.includes(part.methodIndex!)) {
          return state;
        }
        return { ...state, [messageId]: [...current, part.methodIndex!] };
      });
    }
  }

  private handleClarifyVoiceContinuation(): void {
    if (!this.state.isOpen() || this.state.isLoading() || !this.continuousVoiceMode) {
      return;
    }

    const messages = this.state.messages();
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.pending) {
      return;
    }

    if (last.messageContent?.kind === 'clarify') {
      if (last.id === this.lastVoiceContinuationMessageId) {
        return;
      }

      this.lastVoiceContinuationMessageId = last.id;
      this.scheduleScrollToBottom();
      this.resumeVoiceAfterClarify(last);
      return;
    }

    this.continuousVoiceMode = false;
    this.lastVoiceContinuationMessageId = null;
  }

  private resumeVoiceAfterClarify(message: AiHelpMessage): void {
    const startMic = (): void => {
      if (!this.state.isOpen() || this.state.isLoading() || !this.continuousVoiceMode) {
        return;
      }
      this.startVoiceInput();
    };

    if (message.isAudio && message.messageContent) {
      this.speechOutput.speakMessage(message.id, message.messageContent, startMic);
      return;
    }

    queueMicrotask(startMic);
  }

  private startVoiceInput(): void {
    if (this.state.isLoading() || this.speech.isListening() || this.speech.isProcessing()) {
      return;
    }

    this.voiceBaseDraft = this.state.draft().trim();
    this.shouldAutoSendVoice = true;

    const started = this.speech.start(
      (event: AiHelpSpeechTranscriptEvent) => {
        const prefix = this.voiceBaseDraft;
        const combined = prefix ? `${prefix} ${event.transcript}` : event.transcript;
        const question = combined.trim();
        this.draftFromVoice = true;
        this.state.setDraft(question);
        this.resizeComposer();

        if (event.isFinal) {
          this.autoSendVoicePrompt();
        }
      },
      () => {
        this.autoSendVoicePrompt();
      }
    );

    if (!started) {
      this.shouldAutoSendVoice = false;
      this.continuousVoiceMode = false;
    }
  }

  private stopVoiceInput(allowAutoSend: boolean): void {
    if (!allowAutoSend) {
      this.shouldAutoSendVoice = false;
      // Discard in-progress capture (do not transcribe MediaRecorder clips).
      this.speech.abort();
      return;
    }
    this.speech.stop();
  }

  private autoSendVoicePrompt(): void {
    if (!this.shouldAutoSendVoice) {
      return;
    }

    this.shouldAutoSendVoice = false;

    // MediaRecorder has no live interim text. A microtask send clears the draft
    // before paint, so the composer never shows the transcript. Wait one frame
    // (+ brief delay) so the entry field updates first, then auto-send.
    const delayMs = this.speech.engine() === 'media-recorder' ? 450 : 0;
    const runSend = (): void => {
      if (this.state.canSend()) {
        this.onSend(true);
      }
    };

    if (delayMs <= 0) {
      queueMicrotask(runSend);
      return;
    }

    requestAnimationFrame(() => {
      this.resizeComposer();
      this.focusComposer();
      window.setTimeout(runSend, delayMs);
    });
  }

  private static readonly COMPOSER_MAX_HEIGHT_PX = 120;
  private static readonly COMPOSER_MIN_HEIGHT_PX = 28;

  /** Focus the composer. When already focused, keep the current caret/selection. */
  private focusComposer(): void {
    queueMicrotask(() => {
      const el = this.composerInputRef?.nativeElement;
      if (!el || el.disabled) {
        return;
      }

      const alreadyFocused = document.activeElement === el;
      const selectionStart = el.selectionStart;
      const selectionEnd = el.selectionEnd;

      el.focus({ preventScroll: true });

      try {
        if (alreadyFocused) {
          el.setSelectionRange(selectionStart, selectionEnd);
        } else {
          const caret = el.value.length;
          el.setSelectionRange(caret, caret);
        }
      } catch {
        // Some browsers reject setSelectionRange while not focused.
      }
      this.resizeComposer();
    });
  }

  /**
   * Cursor-style composer: grow with content, then lock height and scroll
   * the textarea while mic/send stay fixed below.
   */
  private resizeComposer(): void {
    const el = this.composerInputRef?.nativeElement;
    if (!el) {
      return;
    }

    const maxHeight = AiHelpAssistantPanel.COMPOSER_MAX_HEIGHT_PX;
    const minHeight = AiHelpAssistantPanel.COMPOSER_MIN_HEIGHT_PX;

    el.classList.remove('ai-help-panel__input--scrollable');
    el.style.height = 'auto';

    const contentHeight = el.scrollHeight;
    const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;

    const needsScroll = contentHeight > maxHeight;
    el.classList.toggle('ai-help-panel__input--scrollable', needsScroll);
    el.style.overflowY = needsScroll ? 'auto' : 'hidden';

    if (needsScroll) {
      el.scrollTop = el.scrollHeight;
    }
  }

  /** Show avatar only at the start of a consecutive run from the same sender. */
  shouldShowMessageAvatar(index: number): boolean {
    const messages = this.state.messages();
    const message = messages[index];
    if (!message) {
      return false;
    }
    if (index === 0) {
      return true;
    }
    return messages[index - 1]?.role !== message.role;
  }

  userAvatarInitials(): string {
    return getInitialsFromName(this.userDisplayName());
  }

  userAvatarColor(): string {
    return getAvatarColorFromSeed(this.userAvatarSeed());
  }

  onUserAvatarError(): void {
    this.userAvatarImageFailed.set(true);
  }

  private getUserFirstName(): string {
    const fullName = this.resolveStoredUserName();
    return fullName ? fullName.split(/\s+/)[0] : '';
  }

  private resolveStoredUserName(): string {
    return (
      (this.auth.userData()?.name as string | undefined)?.trim() ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('userName')?.trim() : '') ||
      ''
    );
  }
}
